import { ApiError } from '@epi-helpers/ApiError';
import {
  ValidateUserDataError,
  ValidateUserDataResponse
} from '@epi-models/pages/MassUpload';

const FIELD_NAME_PATTERN = /^\[(\d+)]\.(.+)$/;
const WORKING_HOURS_COLUMN_KEY = 'working_hours_percentage';
const LUNCH_BENEFIT_MONTHLY_DAYS_COLUMN_KEY = 'lunch_benefit_monthly_days';

const parseFieldName = (
  fieldName: string
): { rowIndex: number; columnKey: string } | null => {
  const m = FIELD_NAME_PATTERN.exec(fieldName.trim());
  if (!m) {
    return null;
  }
  return { rowIndex: Number(m[1]), columnKey: m[2] };
};

export const getValidationErrorsFromUnknown = (
  validationResult: unknown
): ValidateUserDataError[] => {
  if (!validationResult || typeof validationResult !== 'object') {
    return [];
  }
  if (validationResult instanceof ApiError) {
    const body = validationResult.prop as { errors?: ValidateUserDataError[] };
    if (Array.isArray(body?.errors)) {
      return body.errors;
    }
  }
  const r = validationResult as Record<string, unknown>;
  if (Array.isArray(r.errors)) {
    return r.errors as ValidateUserDataError[];
  }
  const prop = r.prop as Record<string, unknown> | undefined;
  if (prop && Array.isArray(prop.errors)) {
    return prop.errors as ValidateUserDataError[];
  }
  const data = r.data as Record<string, unknown> | undefined;
  if (data && Array.isArray(data.errors)) {
    return data.errors as ValidateUserDataError[];
  }
  return [];
};

export const getValidationErrorsFromResponse = (
  validationResponse: ValidateUserDataResponse
): ValidateUserDataError[] => {
  const invalidItems = validationResponse.responseItems.filter(
    responseItem => !responseItem.isValid
  );

  return invalidItems.flatMap(responseItem =>
    responseItem.errors.map(error => {
      return {
        severity: 'error',
        fieldName: error.fieldName,
        message: error.errorCodes.join(','),
        stackTrace: '',
        code: error.errorCodes[0] ?? 'validation_error',
        elementIndex: Number.isFinite(responseItem.row_number)
          ? responseItem.row_number - 1
          : undefined
      };
    })
  );
};

export const applyCellErrorsFromIssues = (
  data: Record<string, unknown>[],
  issues: ValidateUserDataError[],
  cellErrorMessage: string
): Record<string, unknown>[] => {
  const next = data.map(row => ({ ...row }));

  for (const issue of issues) {
    const parsed = parseFieldName(issue.fieldName);
    const rowIndex =
      parsed?.rowIndex ??
      (typeof issue.elementIndex === 'number' ? issue.elementIndex : null);
    const columnKey =
      parsed?.columnKey ?? (parsed ? undefined : issue.fieldName?.trim());

    if (
      rowIndex == null ||
      !Number.isFinite(rowIndex) ||
      !columnKey ||
      rowIndex < 0 ||
      rowIndex >= next.length
    ) {
      continue;
    }

    const row = next[rowIndex] as Record<string, unknown>;
    const cell = row[columnKey] as Record<string, unknown> | undefined;
    if (!cell) {
      continue;
    }

    const existingInfo = Array.isArray(cell.info) ? cell.info : [];
    next[rowIndex] = {
      ...row,
      [columnKey]: {
        ...cell,
        info: [
          ...existingInfo.filter(
            (i: { serverValidation?: boolean }) => !i?.serverValidation
          ),
          {
            level: 'error' as const,
            message: cellErrorMessage,
            serverValidation: true
          }
        ]
      }
    };
  }

  return next;
};

export const getLocalIssues = (
  rows: Record<string, unknown>[],
  invalidCellLabel: string
): ValidateUserDataError[] =>
  rows.reduce((acc, row, rowIndex) => {
    const workingHoursValue = (
      row[WORKING_HOURS_COLUMN_KEY] as { value?: unknown }
    )?.value;

    if (
      workingHoursValue !== undefined &&
      workingHoursValue !== null &&
      workingHoursValue !== ''
    ) {
      const numericValue = Number(workingHoursValue);
      const isInvalidRange =
        !Number.isInteger(numericValue) ||
        numericValue < 1 ||
        numericValue > 100;

      if (isInvalidRange) {
        acc.push({
          severity: 'error',
          fieldName: `[${rowIndex}].${WORKING_HOURS_COLUMN_KEY}`,
          message: invalidCellLabel,
          stackTrace: '',
          code: 'working_hours_out_of_range'
        });
      }
    }

    const lunchBenefitMonthlyDaysValue = (
      row[LUNCH_BENEFIT_MONTHLY_DAYS_COLUMN_KEY] as { value?: unknown }
    )?.value;

    if (
      lunchBenefitMonthlyDaysValue !== undefined &&
      lunchBenefitMonthlyDaysValue !== null &&
      lunchBenefitMonthlyDaysValue !== ''
    ) {
      const lunchBenefitMonthlyDaysNumericValue = Number(
        lunchBenefitMonthlyDaysValue
      );
      const isLunchBenefitMonthlyDaysInvalidRange =
        !Number.isInteger(lunchBenefitMonthlyDaysNumericValue) ||
        lunchBenefitMonthlyDaysNumericValue < 1 ||
        lunchBenefitMonthlyDaysNumericValue > 23;

      if (isLunchBenefitMonthlyDaysInvalidRange) {
        acc.push({
          severity: 'error',
          fieldName: `[${rowIndex}].${LUNCH_BENEFIT_MONTHLY_DAYS_COLUMN_KEY}`,
          message: invalidCellLabel,
          stackTrace: '',
          code: 'lunch_benefit_monthly_days_out_of_range'
        });
      }
    }

    return acc;
  }, [] as ValidateUserDataError[]);
